import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './MundialPage.css';

const API = process.env.REACT_APP_API_URL || '/api';

const STAGE_ORDER = ['Group Stage', 'Round of 32', 'Round of 16', 'Quarter-final', 'Semi-final', 'Third Place', 'Final'];

const STAGE_KEYS = {
  'Group Stage':    'mundial.stage_group',
  'Round of 32':   'mundial.stage_r32',
  'Round of 16':   'mundial.stage_r16',
  'Quarter-final': 'mundial.stage_qf',
  'Semi-final':    'mundial.stage_sf',
  'Third Place':   'mundial.stage_3rd',
  'Final':         'mundial.stage_final',
};

const FLAG_MAP = {
  'Argentina':      'ar', 'Brazil':         'br', 'France':          'fr',
  'England':        'gb-eng','Germany':      'de', 'Italy':           'it',
  'Spain':          'es', 'Netherlands':    'nl', 'Portugal':        'pt',
  'Uruguay':        'uy', 'United States':  'us', 'Mexico':          'mx',
  'Belgium':        'be', 'Croatia':        'hr', 'Poland':          'pl',
  'Switzerland':    'ch', 'Sweden':         'se', 'Denmark':         'dk',
  'Senegal':        'sn', 'Morocco':        'ma', 'Japan':           'jp',
  'South Korea':    'kr', 'Australia':      'au', 'Ghana':           'gh',
  'Cameroon':       'cm', 'Nigeria':        'ng', 'Ecuador':         'ec',
  'Colombia':       'co', 'Chile':          'cl', 'Peru':            'pe',
  'Russia':         'ru', 'Czech Republic': 'cz', 'Hungary':         'hu',
  'Romania':        'ro', 'Turkey':         'tr', 'Costa Rica':      'cr',
  'Iran':           'ir', 'Serbia':         'rs', 'Qatar':           'qa',
  'Saudi Arabia':   'sa', 'Ivory Coast':    'ci', 'Tunisia':         'tn',
  'Algeria':        'dz', 'Egypt':          'eg', 'Scotland':        'gb-sct',
  'Wales':          'gb-wls','Ireland':      'ie', 'Austria':         'at',
  'Bulgaria':       'bg', 'Slovakia':       'sk', 'Slovenia':        'si',
  'Paraguay':       'py', 'Bolivia':        'bo', 'Honduras':        'hn',
  'Jamaica':        'jm', 'El Salvador':    'sv', 'Haiti':           'ht',
  'Canada':         'ca', 'Panama':         'pa', 'Kuwait':          'kw',
  'Iraq':           'iq', 'Indonesia':      'id', 'China':           'cn',
  'Norway':         'no', 'Finland':        'fi', 'Greece':          'gr',
  'Yugoslavia':     'rs', 'Czechoslovakia': 'cz', 'Soviet Union':    'ru',
  'East Germany':   'de', 'West Germany':   'de', 'DR Congo':        'cd',
  'New Zealand':    'nz', 'Ukraine':        'ua', 'Angola':          'ao',
  'Togo':           'tg', 'Bosnia and Herzegovina': 'ba',
  'Northern Ireland': 'gb-nir', 'United Arab Emirates': 'ae',
  'Venezuela': 've',
  'South Africa': 'za', 'Cape Verde': 'cv', 'Curaçao': 'cw',
  'Jordan': 'jo', 'Uzbekistan': 'uz',
};

function Flag({ name, size = 'sm' }) {
  const code = FLAG_MAP[name];
  if (!code) return null;
  return <span className={`fi fi-${code} wc-flag wc-flag-${size}`} title={name} />;
}

// ── Prediction badge (2026 group matches) ────────────────────────────────────
function PredictionBadge({ prediction }) {
  const { t } = useTranslation();
  const pct = v => `${Math.round(v * 100)}%`;
  const outcome = { H: t('mundial.stat_home'), D: t('mundial.draw'), A: t('mundial.stat_away') }[prediction.pred];
  const title = t('mundial.pred_tooltip', {
    outcome, home: pct(prediction.prob_H), draw: pct(prediction.prob_D), away: pct(prediction.prob_A),
  });
  return (
    <span className={`wc-pred-badge ${prediction.hit ? 'wc-pred-hit' : 'wc-pred-miss'}`} title={title}>
      {prediction.hit ? '✓' : '✗'}
    </span>
  );
}

// ── Match row ────────────────────────────────────────────────────────────────
function MatchRow({ match }) {
  const isHome   = match.result === 'H';
  const isAway   = match.result === 'A';
  const hasScore = match.home_score != null && match.away_score != null;

  return (
    <div className={`wc-match-row${!hasScore ? ' wc-upcoming' : ''}`}>
      <span className="wc-match-date">{match.date}</span>
      {match.group && (
        <span className="wc-match-group-tag">{match.group}</span>
      )}
      <div className="wc-teams">
        <span className={`wc-team wc-team-home ${isHome ? 'wc-winner' : ''}`}>
          <Flag name={match.home_team} />
          <span className="wc-team-name">{match.home_team}</span>
        </span>
        <span className="wc-scorebox">
          {hasScore ? (
            <>
              <span className="wc-score-num">{match.home_score}</span>
              <span className="wc-score-sep">–</span>
              <span className="wc-score-num">{match.away_score}</span>
              {match.extra_time && <span className="wc-badge wc-aet">AET</span>}
              {match.penalties  && <span className="wc-badge wc-pen">PKS</span>}
            </>
          ) : (
            <span className="wc-vs-label">vs</span>
          )}
        </span>
        <span className={`wc-team wc-team-away ${isAway ? 'wc-winner' : ''}`}>
          <span className="wc-team-name">{match.away_team}</span>
          <Flag name={match.away_team} />
        </span>
      </div>
      <span className="wc-elos">{match.home_elo} · {match.away_elo}</span>
      {match.prediction && <PredictionBadge prediction={match.prediction} />}
    </div>
  );
}

// ── Group card ───────────────────────────────────────────────────────────────
// `advancing`: set of teams that went through to the knockout stage. When it's
// missing (older editions) the top 2 of each group are the ones that advance.
function GroupCard({ groupName, groupData, advancing }) {
  const { t } = useTranslation();
  const { matches, standings } = groupData;
  const [open, setOpen] = useState(false);
  const [tab,  setTab]  = useState('tabla');
  const advances = (row, i) => (advancing ? advancing.has(row.team) : i < 2);
  const qualified = standings.filter(advances).map(r => r.team);

  return (
    <div className={`wc-group-card ${open ? 'open' : ''}`}>
      <button className="wc-group-header" onClick={() => setOpen(o => !o)}>
        <span className="wc-group-name">{groupName}</span>
        <span className="wc-group-meta">{standings.map(r => r.team).join(' · ')}</span>
        <span className="wc-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="wc-group-body">
          <div className="wc-tab-switcher">
            <button className={`wc-tab-btn ${tab === 'tabla' ? 'active' : ''}`} onClick={() => setTab('tabla')}>{t('mundial.tab_standings')}</button>
            <button className={`wc-tab-btn ${tab === 'partidos' ? 'active' : ''}`} onClick={() => setTab('partidos')}>{t('mundial.tab_matches')}</button>
          </div>
          {tab === 'tabla' && (
            <div className="wc-standings-wrap">
              <table className="wc-standings">
                <thead><tr>
                  <th>#</th><th className="wc-th-team">{t('mundial.col_team')}</th><th>{t('mundial.col_played')}</th>
                  <th>{t('mundial.col_won')}</th><th>{t('mundial.col_drawn')}</th><th>{t('mundial.col_lost')}</th>
                  <th>{t('mundial.col_gf')}</th><th>{t('mundial.col_ga')}</th><th>{t('mundial.col_gd')}</th><th>{t('mundial.col_pts')}</th>
                </tr></thead>
                <tbody>
                  {standings.map((row, i) => (
                    <tr key={row.team} className={advances(row, i) ? 'wc-qualified' : 'wc-eliminated'}>
                      <td className="wc-pos">{i + 1}</td>
                      <td className="wc-td-team"><Flag name={row.team} size="xs" /><span>{row.team}</span></td>
                      <td>{row.PJ}</td>
                      <td>{row.PG}</td><td>{row.PE}</td><td>{row.PP}</td>
                      <td>{row.GF}</td><td>{row.GC}</td>
                      <td className={row.DG > 0 ? 'wc-dg-pos' : row.DG < 0 ? 'wc-dg-neg' : ''}>{row.DG > 0 ? `+${row.DG}` : row.DG}</td>
                      <td className="wc-pts">{row.Pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="wc-qualified-note">
                {t('mundial.qualified', { teams: qualified.join(` ${t('mundial.and')} `) })}
              </p>
            </div>
          )}
          {tab === 'partidos' && (
            <div className="wc-group-matches">{matches.map((m, i) => <MatchRow key={i} match={m} />)}</div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupStageSection({ stageData, totalMatches, advancing }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const groupNames = Object.keys(stageData.groups).sort();
  return (
    <div className="wc-stage-section">
      <button className="wc-stage-header" onClick={() => setOpen(o => !o)}>
        <span className="wc-stage-label">{t('mundial.group_stage')}</span>
        <span className="wc-stage-count">{t('mundial.matches_groups', { matches: totalMatches, groups: groupNames.length })}</span>
        <span className="wc-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="wc-groups-grid">
          {groupNames.map(g => <GroupCard key={g} groupName={g} groupData={stageData.groups[g]} advancing={advancing} />)}
        </div>
      )}
    </div>
  );
}

function KnockoutSection({ stage, stageData }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const count = stageData.matches.length;
  return (
    <div className="wc-stage-section">
      <button className="wc-stage-header" onClick={() => setOpen(o => !o)}>
        <span className="wc-stage-label">{t(STAGE_KEYS[stage] || 'mundial.stage_group')}</span>
        <span className="wc-stage-count">{t('mundial.match', { count })}</span>
        <span className="wc-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="wc-knockout-matches">
          {stageData.matches.map((m, i) => <MatchRow key={i} match={m} />)}
        </div>
      )}
    </div>
  );
}

function EditionStats({ editionData }) {
  const { t } = useTranslation();
  const allMatches = [];
  Object.values(editionData.stages).forEach(s => {
    if (s.type === 'group_stage') Object.values(s.groups).forEach(g => allMatches.push(...g.matches));
    else allMatches.push(...s.matches);
  });
  const total = allMatches.length;
  const homeWins = allMatches.filter(m => m.result === 'H').length;
  const draws    = allMatches.filter(m => m.result === 'D').length;
  const awayWins = allMatches.filter(m => m.result === 'A').length;
  const goals    = allMatches.reduce((s, m) => s + (m.home_score ?? 0) + (m.away_score ?? 0), 0);
  return (
    <div className="wc-stats-bar">
      {[
        { label: t('mundial.stat_matches'), value: total,                        cls: '' },
        { label: t('mundial.stat_goals'),   value: goals,                        cls: '' },
        { label: t('mundial.stat_avg'),     value: (goals / total).toFixed(2),   cls: '' },
        { label: t('mundial.stat_home'),    value: homeWins,                     cls: 'stat-home' },
        { label: t('mundial.stat_draw'),    value: draws,                        cls: 'stat-draw' },
        { label: t('mundial.stat_away'),    value: awayWins,                     cls: 'stat-away' },
      ].map(({ label, value, cls }) => (
        <div className="wc-stat" key={label}><span className="wc-stat-label">{label}</span><span className={`wc-stat-value ${cls}`}>{value}</span></div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// 2026: OUR PREDICTION VS WHAT HAPPENED
// ════════════════════════════════════════════════════════════════════════════

// ── Projected bracket (frozen before the tournament) ─────────────────────────
function KnockoutMatchCard({ match, label, actualTeams }) {
  const ph = Math.round(match.prob_home * 100);
  const pa = Math.round(match.prob_away * 100);
  // Teams that really reached this round are marked as a hit
  const cls = team => [
    match.predicted_winner === team ? 'ko-fav' : '',
    actualTeams?.has(team) ? 'ko-hit' : '',
  ].join(' ');
  return (
    <div className="ko-match-card">
      {label && <div className="ko-match-label">{label}</div>}
      <div className={`ko-team ${cls(match.home)}`}>
        <Flag name={match.home} size="xs" />
        <span className="ko-team-name">{match.home}</span>
        <span className="ko-pct">{ph}%</span>
      </div>
      <div className="ko-vs-sep">vs</div>
      <div className={`ko-team ${cls(match.away)}`}>
        <Flag name={match.away} size="xs" />
        <span className="ko-team-name">{match.away}</span>
        <span className="ko-pct">{pa}%</span>
      </div>
      <div className="ko-winner">→ {match.predicted_winner}</div>
    </div>
  );
}

function ProjectedBracket({ knockout, rounds }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Teams that really played each round, keyed like the bracket rounds
  const roundKeys = { 'Round of 32': 'r32', 'Round of 16': 'r16', 'Quarter-final': 'qf', 'Semi-final': 'sf', 'Final': 'final' };
  const actualByKey = {};
  rounds.forEach(r => { actualByKey[roundKeys[r.stage]] = new Set(r.actual); });

  const stage = (key, name) => knockout[key]?.length > 0 && (
    <div className={`ko-stage ${key === 'final' ? 'ko-final-stage' : ''}`}>
      <div className={`ko-stage-title ${key === 'final' ? 'ko-final-title' : ''}`}>{name}</div>
      <div className="ko-stage-matches">
        {knockout[key].map((m, i) => (
          <KnockoutMatchCard key={i} match={m} label={key === 'final' ? null : `M${i + 1}`} actualTeams={actualByKey[key]} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="wc-stage-section">
      <button className="wc-stage-header" onClick={() => setOpen(o => !o)}>
        <span className="wc-stage-label">{t('mundial.projected_bracket')}</span>
        <span className="wc-stage-count">{t('mundial.projected_bracket_sub')}</span>
        <span className="wc-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="ko-wrapper">
          <p className="ko-disclaimer">{t('mundial.projected_bracket_note')}</p>
          {stage('r32',   t('mundial.round_32'))}
          {stage('r16',   t('mundial.round_16'))}
          {stage('qf',    t('mundial.quarter_final'))}
          {stage('sf',    t('mundial.semi_final'))}
          {stage('final', t('mundial.final'))}
        </div>
      )}
    </div>
  );
}

// ── Prediction summary card ──────────────────────────────────────────────────
function Prediction2026({ prediction }) {
  const { t } = useTranslation();
  const { group_stage, rounds, predicted_champion, actual_champion } = prediction;
  const pct = (hits, total) => `${Math.round((hits / total) * 100)}%`;
  const championHit = predicted_champion === actual_champion;

  return (
    <div className="wc-pred-summary">
      <h4 className="wc-pred-title">{t('mundial.prediction_title')}</h4>
      <p className="wc-pred-note">{t('mundial.prediction_note')}</p>

      <div className={`wc-pred-champion ${championHit ? 'wc-pred-hit' : 'wc-pred-miss'}`}>
        <span>{t('mundial.predicted_champion')}: <Flag name={predicted_champion} /> <strong>{predicted_champion}</strong></span>
        <span>{t('mundial.actual_champion')}: <Flag name={actual_champion} /> <strong>{actual_champion}</strong></span>
        <span className="wc-pred-champion-mark">{championHit ? '✓' : '✗'}</span>
      </div>

      <div className="wc-stats-bar">
        <div className="wc-stat">
          <span className="wc-stat-label">{t('mundial.pred_group_matches')}</span>
          <span className="wc-stat-value">{group_stage.hits}/{group_stage.total} · {pct(group_stage.hits, group_stage.total)}</span>
        </div>
        {rounds.map(r => (
          <div className="wc-stat" key={r.stage}>
            <span className="wc-stat-label">{t('mundial.pred_reached', { stage: t(STAGE_KEYS[r.stage]) })}</span>
            <span className="wc-stat-value">{r.hits}/{r.total}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export function MundialPage() {
  const { t } = useTranslation();
  const [editions, setEditions]       = useState([]);
  const [selected, setSelected]       = useState(null);
  const [editionData, setEditionData] = useState(null);
  const [prediction, setPrediction]   = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  useEffect(() => {
    axios.get(`${API}/worldcup/editions`)
      .then(r => {
        const eds = r.data.editions.slice().reverse();
        setEditions(eds);
        setSelected(eds[0] || null);
      })
      .catch(() => setError(t('mundial.error_editions')));
  }, [t]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    setEditionData(null);
    setPrediction(null);
    axios.get(`${API}/worldcup/history?edition=${selected}`)
      .then(r => setEditionData(r.data.data[selected] || null))
      .catch(() => setError(t('mundial.error_edition_data')))
      .finally(() => setLoading(false));
    // Only 2026 has a saved prediction; if it fails the results still show
    if (selected === 2026) {
      axios.get(`${API}/worldcup/2026/prediction`)
        .then(r => setPrediction(r.data))
        .catch(() => setPrediction(null));
    }
  }, [selected, t]);

  // Since 2026 the best third-placed teams also advance, so take the
  // qualifiers from the Round of 32 instead of the group top 2
  const r32 = editionData?.stages['Round of 32'];
  const advancing = r32 ? new Set(r32.matches.flatMap(m => [m.home_team, m.away_team])) : null;

  return (
    <div className="mundial-page">
      <div className="mundial-header">
        <h2 className="mundial-title">{t('mundial.page_title')}</h2>
        <p className="mundial-subtitle">{t('mundial.page_subtitle')}</p>
      </div>

      <div className="wc-edition-selector">
        {editions.map(ed => (
          <button
            key={ed}
            className={`wc-ed-btn ${selected === ed ? 'active' : ''}`}
            onClick={() => setSelected(ed)}
          >
            {ed}
          </button>
        ))}
      </div>

      {error && <div className="wc-status wc-error">{error}</div>}

      {loading && <div className="wc-status wc-loading">{t('mundial.loading')}</div>}
      {!loading && editionData && (
        <div className="wc-edition-content">
          <h3 className="wc-edition-title">{t('mundial.title_edition', { year: selected })}</h3>
          <EditionStats editionData={editionData} />
          {prediction && <Prediction2026 prediction={prediction} />}
          <div className="wc-stages-list">
            {STAGE_ORDER.map(stage => {
              const s = editionData.stages[stage];
              if (!s) return null;
              if (s.type === 'group_stage') {
                const totalGS = Object.values(s.groups).reduce((n, g) => n + g.matches.length, 0);
                return <GroupStageSection key={stage} stageData={s} totalMatches={totalGS} advancing={advancing} />;
              }
              return <KnockoutSection key={stage} stage={stage} stageData={s} />;
            })}
            {prediction && <ProjectedBracket knockout={prediction.knockout} rounds={prediction.rounds} />}
          </div>
        </div>
      )}
    </div>
  );
}
